// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, layoutContainer, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    widgetId: string;
}

interface FooterCopyrightSectionStyles {
    container?: InjectableCss;
    innerContainer?: InjectableCss;
    copyrightText?: TypographyPresentationProps;
}

const bottomNavLinkStyles = css`
    & nav[class^="ListNav"] {
        padding: 0;
        text-align: right;
        .link-list-items {
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 8px 16px;
            ${xScreen("lte", "sm")} {
                justify-content: center;
            }

            li:empty {
                display: none;
            }
            li {
                padding: 0;
                line-height: ${fontConfig.singleLineSmall.lineHeight};

                .item-link {
                    ${TypographyStyle} {
                        color: ${color("common.white")};
                        font-size: ${fontConfig.singleLineSmall.size}px;
                        line-height: ${fontConfig.singleLineSmall.lineHeight};
                        font-weight: ${fontConfig.weight.regular};
                        text-transform: capitalize;
                        margin-bottom: 0;
                    }
                    &:hover ${TypographyStyle} {
                        color: ${color("text.link")};
                    }
                }
            }
        }
    }
`;

const footerCopyrightStyles: FooterCopyrightSectionStyles = {
    container: {
        css: css`
            background: ${color("common.ui6")};
            padding: 16px 0;
        `,
    },
    innerContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px 20px;
            ${xScreen("lte", "sm")} {
                flex-direction: column-reverse;
                justify-content: center;
            }
            ${layoutContainer}
            ${bottomNavLinkStyles}
        `,
    },
    copyrightText: {
        size: fontConfig.singleLineSmall.size,
        lineHeight: fontConfig.singleLineSmall.lineHeight,
        weight: fontConfig.weight.regular,
        color: "common.white",
        css: css`
            white-space: nowrap;
            ${xScreen("lte", "sm")} {
                white-space: pre-wrap;
            }
        `,
    },
};

const styles = footerCopyrightStyles;
const FooterCopyrightSection = ({ widgetId }: Props) => {
    const copyrightMessage = () => {
        const message = siteMessage("Custom_Footer_Copyright").toString() || "© {0} EIS Inc. All Rights Reserved.";
        return message.replace("{0}", String(new Date().getFullYear()));
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="FooterCopyrightSection">
            <StyledWrapper {...styles.innerContainer}>
                <Typography {...styles.copyrightText}>{copyrightMessage()}</Typography>
                <Zone zoneName="RichContent" contentId={widgetId} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default FooterCopyrightSection;
